import { PlanningPageType } from './PlanningPage';

export const RESPONSIBLE_ROLE = {
  PSC: 'PSC',
  SITL: 'SITL',
  RESL: 'RESL',
  IC_UC: 'IC/UC',
  COML: 'COML',
  AOBD: 'AOBD',
  ENVL: 'ENVL',
  FACL: 'FACL',
  DMOB: 'DMOB',
  SUPERVISOR: 'SUPERVISOR',
} as const;
export type ResponsibleRoleType =
  (typeof RESPONSIBLE_ROLE)[keyof typeof RESPONSIBLE_ROLE];

export const PHASE_LIST = [
  'IC/UC Objectives',
  'Command & General Staff Meeting',
  'Prepare Tactics',
  'Tactics Meeting',
  'Prepare Planning',
  'Planning Meeting',
  'IAP Prep & Approval',
  'Operations Briefing',
];

export const PLANNING_PHASE = {
  IC_UC_OBJECTIVES: 'IC/UC Objectives',
  COMMAND_GENERAL_STAFF_MEETING: 'Command & General Staff Meeting',
  PREPARE_TACTICS: 'Prepare Tactics',
  TACTICS_MEETING: 'Tactics Meeting',
  PREPARE_PLANNING: 'Prepare Planning',
  PLANNING_MEETING: 'Planning Meeting',
  IAP_PREP_APPROVAL: 'IAP Prep & Approval',
  OPERATIONS_BRIEFING: 'Operations Briefing',
} as const;
export type PlanningPhaseType =
  (typeof PLANNING_PHASE)[keyof typeof PLANNING_PHASE];

export const AGENDA_ITEM_TEXT = {
  /** CG-IMH Ch.4 — comma after “Roll call” per handbook tables */
  ROLL_CALL_GROUND_RULES: 'Roll call, review ground rules and meeting agenda.',
  SITUATION_BRIEF: 'Provide situation brief.',
  OPEN_ACTION_TRACKER: 'Review and update Open Action Tracker (ICS 233).',
  DECISIONS_MEETING_SCHEDULE:
    'Review decisions and meeting schedule (ICS 230).',
  BRIEF_CLOSING_COMMENTS: 'Provide brief closing comments.',
  CLOSING_COMMENTS: 'Provide closing comments.',
  IC_UC_COMMENTS: 'Provide IC/UC comments.',
  REVIEW_IC_UC_OBJECTIVES_CIR_IAP_CHANGES:
    'Review IC/UC objectives, operational CIRs and changes to the IAP (e.g., pen and ink changes).',
} as const;

/**
 * Standard meeting ground rules shown under the roll-call agenda line (CG-IMH
 * practice).
 */
export const imhMeetingGroundRules: readonly string[] = [
  'Arrive on time.',
  'Silence Devices (cell phones, radios, etc).',
  'Respect the agenda and meeting purpose.',
  'No sidebar conversations.',
  'No use of social media.',
];

/** Card titles for the 234 and the 215, which each stand on their own. */
export const incidentCardWorkAnalysisMatrixTitle =
  'Work Analysis Matrix ICS-234';
export const incidentCardOperationalPlanningWorksheetTitle =
  'Operational Planning Worksheet ICS-215';
export const incidentCardCommunicationsList205aTitle =
  'Communications List ICS-205A';

export const WIDGET_TITLES = [
  { widgetName: 'agenda', title: 'Agenda' },
  { widgetName: 'assignmentList', title: 'Assignment List (ICS-204)' },
  {
    widgetName: 'iapSafetyAnalysis',
    title: 'Incident Action Plan Safety Analysis ICS-215A',
  },
  { widgetName: 'incidentActionPlan', title: 'Incident Action Plan (IAP)' },
  {
    widgetName: 'incidentObjectives',
    title: 'Incident Objectives ICS-202',
  },
  {
    widgetName: 'incidentRadioCommunicationsPlan',
    title: 'Incident Radio Communications Plan ICS-205',
  },
  {
    widgetName: 'incidentStatusSummary',
    title: 'Incident Status Summary ICS-209',
  },
  { widgetName: 'initialResponse', title: 'Incident Briefing ICS-201' },
  { widgetName: 'medicalPlan', title: 'Medical Plan ICS-206' },
  {
    widgetName: 'openActionsTracker',
    title: 'Incident Open Action Tracker ICS-233CG',
  },
  {
    widgetName: 'organizationAssignmentList',
    title: 'Organization Assignment List ICS-203',
  },
  { widgetName: 'safetyPlan', title: 'Safety Message-Plan ICS-208' },
  {
    widgetName: 'hazardousMaterials',
    title: `Hazardous Materials ICS-208HM`,
  },
  /*
   * The combined `workAnalysis` card, which paired the 234 and the 215 in one
   * pane, is deliberately absent. Both forms now stand on their own below, each
   * with its own versioning and signature, so the combined view has no way to
   * say which of the two a signature applies to. `renderWidget` still handles
   * it and `UnifiedWorkView` still exists, so anything holding a saved
   * `workAnalysis` card keeps rendering; it is just no longer selectable.
   */
  {
    widgetName: 'workAnalysisMatrix',
    title: incidentCardWorkAnalysisMatrixTitle,
  },
  {
    widgetName: 'operationalPlanningWorksheet',
    title: incidentCardOperationalPlanningWorksheetTitle,
  },
  {
    widgetName: 'communicationsList205a',
    title: incidentCardCommunicationsList205aTitle,
  },
  {
    widgetName: 'commsResourceAvailability',
    title: 'Communications Resource Availability ICS-217A',
  },
  {
    widgetName: 'airOperationsSummary',
    title: 'Air Operations Summary ICS-220',
  },
  {
    widgetName: 'uasAirOperationsSummary',
    title: 'UAS Air Operations Summary ICS-220UAS',
  },
  {
    widgetName: 'personnelPerformanceRating',
    title: 'Personnel Performance Rating ICS-225',
  },
  {
    widgetName: 'resourcesAtRisk',
    title: 'Resources at Risk Summary ICS-232',
  },
  {
    widgetName: 'acpSiteIndex',
    title: 'ACP Site Index ICS-232A',
  },
  {
    widgetName: 'facilityNeedsAssessment',
    title: 'Facility Needs Assessment ICS-235',
  },
  {
    widgetName: 'tentativeReleaseList',
    title: 'Tentative Release List ICS-236',
  },
  {
    widgetName: 'accountableResourceTracking',
    title: 'Incident Accountable Resource Tracking Sheet ICS-261',
  },
  {
    widgetName: 'cg5136',
    title: 'Federal Project Cost Summary CG-5136',
  },
  {
    widgetName: 'cg5136x',
    title: 'Federal Project Cost Summary CG-5136X',
  },
  /*
   * Read-only views of forms authored elsewhere — the roster, check-in,
   * resource requests, activity logs, check-out and the calendar. They show
   * the printed sheet without moving where it is edited.
   */
  {
    widgetName: 'incidentOrganizationChart',
    title: 'Incident Organization Chart ICS-207',
  },
  { widgetName: 'checkInList', title: 'Incident Check-in List ICS-211' },
  {
    widgetName: 'dailyPersonnelSignIn',
    title: 'Daily Personnel Sign-In Sheet ICS-211A',
  },
  { widgetName: 'resourceRequestForm', title: 'Resource Request ICS-213RR' },
  { widgetName: 'activityLogDocument', title: 'Activity Log ICS-214' },
  {
    widgetName: 'demobilizationCheckOut',
    title: 'Demobilization Check-Out ICS-221',
  },
  {
    widgetName: 'demobTrackingTable',
    title: 'Demob Tracking Table ICS-238',
  },
  {
    widgetName: 'dailyMeetingSchedule',
    title: 'Daily Meeting Schedule ICS-230',
  },
];

/** Display title for an incident planning card (matches phase selector buttons). */
export function getIncidentCardTitle(widgetName: string): string {
  const found = WIDGET_TITLES.find((w) => w.widgetName === widgetName);
  return found?.title ?? widgetName;
}

/**
 * Short label for the Planning P rail (full title stays in tooltips / Forms
 * menu).
 */
const INCIDENT_CARD_RAIL_LABELS: Record<string, string> = {
  agenda: 'Agenda',
  assignmentList: 'ICS-204',
  iapSafetyAnalysis: 'ICS-215A',
  incidentActionPlan: 'IAP',
  incidentObjectives: 'ICS-202',
  incidentRadioCommunicationsPlan: 'ICS-205',
  incidentStatusSummary: 'ICS-209',
  initialResponse: 'ICS-201',
  medicalPlan: 'ICS-206',
  openActionsTracker: 'ICS-233CG',
  organizationAssignmentList: 'ICS-203',
  safetyPlan: 'ICS-208',
  hazardousMaterials: 'ICS-208HM',
  workAnalysis: 'ICS-234 / ICS-215',
  workAnalysisMatrix: 'ICS-234',
  operationalPlanningWorksheet: 'ICS-215',
  communicationsList205a: 'ICS-205A',
  commsResourceAvailability: 'ICS-217A',
  airOperationsSummary: 'ICS-220',
  uasAirOperationsSummary: 'ICS-220UAS',
  personnelPerformanceRating: 'ICS-225',
  resourcesAtRisk: 'ICS-232',
  acpSiteIndex: 'ICS-232A',
  facilityNeedsAssessment: 'ICS-235',
  tentativeReleaseList: 'ICS-236',
  accountableResourceTracking: 'ICS-261',
  cg5136: 'CG-5136',
  cg5136x: 'CG-5136X',
  incidentOrganizationChart: 'ICS-207',
  checkInList: 'ICS-211',
  dailyPersonnelSignIn: 'ICS-211A',
  resourceRequestForm: 'ICS-213RR',
  activityLogDocument: 'ICS-214',
  demobilizationCheckOut: 'ICS-221',
  demobTrackingTable: 'ICS-238',
  dailyMeetingSchedule: 'ICS-230',
};

/**
 * Cards a FEMA incident does not offer. They stay on the Coast Guard create
 * lists because that family's form set is complete.
 */
export const USCG_ONLY_FORM_WIDGETS = [
  'accountableResourceTracking',
  'cg5136',
  'cg5136x',
] as const;

export function widgetsHiddenForFamily(family: 'uscg' | 'fema'): string[] {
  return family === 'fema' ? [...USCG_ONLY_FORM_WIDGETS] : [];
}

export function getIncidentCardRailLabel(widgetName: string): string {
  return (
    INCIDENT_CARD_RAIL_LABELS[widgetName] ?? getIncidentCardTitle(widgetName)
  );
}

export const PLANNING_PHASES: PlanningPageType[] = [
  {
    title: PLANNING_PHASE.IC_UC_OBJECTIVES,
    description: ' ',
    widgets: {
      agenda: true,
      incidentObjectives: true,
      openActionsTracker: true,
      incidentStatusSummary: true,
      dailyMeetingSchedule: true,
    },
    widgetOrder: [
      'agenda',
      'incidentObjectives',
      'openActionsTracker',
      'incidentStatusSummary',
      'dailyMeetingSchedule',
    ],
    code: '#1E88E5', // Blue
    agenda: [
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.ROLL_CALL_GROUND_RULES,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Review incident priorities and Command Direction.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Review incident objectives and update as needed.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Review and/or update key decisions and IMT procedures.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Identify Limitations and Constraints.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Develop CIRs and time critical expectations.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Develop or update key procedures: managing sensitive information.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Develop or update key procedures: resource request and ordering process.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Develop or update key procedures: cost sharing and cost accounting.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Agree on division of UC workload.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.OPEN_ACTION_TRACKER,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.DECISIONS_MEETING_SCHEDULE,
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: AGENDA_ITEM_TEXT.CLOSING_COMMENTS,
      },
    ],
  },
  {
    title: PLANNING_PHASE.COMMAND_GENERAL_STAFF_MEETING,
    description: ' ',
    widgets: {
      agenda: true,
      incidentObjectives: true,
      openActionsTracker: true,
      incidentStatusSummary: true,
    },
    widgetOrder: [
      'agenda',
      'incidentObjectives',
      'openActionsTracker',
      'incidentStatusSummary',
    ],
    code: '#43A047', // Green
    agenda: [
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.ROLL_CALL_GROUND_RULES,
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: AGENDA_ITEM_TEXT.IC_UC_COMMENTS,
      },
      {
        role: RESPONSIBLE_ROLE.SITL,
        item: AGENDA_ITEM_TEXT.SITUATION_BRIEF,
      },
      {
        role: 'SOFR',
        item: 'Provide Safety briefing highlighting any near misses or injuries requiring medical attention beyond first aid and safety issues.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Present priorities and operational emphasis.',
      },
      { role: RESPONSIBLE_ROLE.IC_UC, item: 'Present incident objectives.' },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Present key decisions and procedures.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Present CIRs and their time criticality.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Identify limitations and constraints.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Assign or review functional tasks/open actions to Command and General Staff.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Open discussion to clarify priorities, objectives, assignments, issues, concerns, and open actions/tasks.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.OPEN_ACTION_TRACKER,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.DECISIONS_MEETING_SCHEDULE,
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: AGENDA_ITEM_TEXT.BRIEF_CLOSING_COMMENTS,
      },
    ],
  },
  {
    title: PLANNING_PHASE.PREPARE_TACTICS,
    description: ' ',
    widgets: {
      workAnalysisMatrix: true,
      operationalPlanningWorksheet: true,
      iapSafetyAnalysis: true,
      incidentStatusSummary: true,
      openActionsTracker: true,
      resourcesAtRisk: true,
      acpSiteIndex: true,
    },
    widgetOrder: [
      'workAnalysisMatrix',
      'operationalPlanningWorksheet',
      'iapSafetyAnalysis',
      'incidentStatusSummary',
      'openActionsTracker',
      'resourcesAtRisk',
      'acpSiteIndex',
    ],
    code: '#FB8C00', // Orange
    agenda: [],
  },
  {
    title: PLANNING_PHASE.TACTICS_MEETING,
    description: ' ',
    widgets: {
      agenda: true,
      workAnalysisMatrix: true,
      operationalPlanningWorksheet: true,
      openActionsTracker: true,
      iapSafetyAnalysis: true,
      incidentObjectives: true,
      incidentStatusSummary: true,
    },
    widgetOrder: [
      'agenda',
      'incidentObjectives',
      'workAnalysisMatrix',
      'operationalPlanningWorksheet',
      'iapSafetyAnalysis',
      'openActionsTracker',
      'incidentStatusSummary',
    ],
    code: '#F4511E', // Deep Orange
    agenda: [
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.ROLL_CALL_GROUND_RULES,
      },
      {
        role: RESPONSIBLE_ROLE.SITL,
        item: AGENDA_ITEM_TEXT.SITUATION_BRIEF,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Review incident operational objectives. (ICS 202).',
      },
      {
        role: 'OSC/ISC',
        item: 'Review Work Analysis Matrix (ICS 234) strategies and tactics. Update Operations Section organization chart and Intelligence / Investigations Section organization chart (if established). Review and complete the Operational Planning Worksheet (ICS 215) which addresses work assignments, resource commitments, contingencies, and required support facilities.',
      },
      {
        role: 'SOFR',
        item: 'Complete Incident Action Plan Safety Analysis (ICS 215A) and identify and resolve any critical safety issues.',
      },
      {
        role: 'ENVL',
        item: 'Discuss and resolve any environmental issues (as required).',
      },
      {
        role: 'OSC/PSC',
        item: 'Validate linkage between proposed tactics and operational objectives.',
      },
      {
        role: RESPONSIBLE_ROLE.RESL,
        item: 'Verify needed incident resources.',
      },
      {
        role: 'LSC',
        item: 'Discuss and resolve any logistics issues.',
      },
      {
        role: 'FSC',
        item: 'Discuss and resolve any finance issues.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.OPEN_ACTION_TRACKER,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.DECISIONS_MEETING_SCHEDULE,
      },
    ],
  },
  {
    title: PLANNING_PHASE.PREPARE_PLANNING,
    description: ' ',
    widgets: {
      workAnalysisMatrix: true,
      operationalPlanningWorksheet: true,
      openActionsTracker: true,
      assignmentList: true,
      iapSafetyAnalysis: true,
      incidentStatusSummary: true,
      commsResourceAvailability: true,
      facilityNeedsAssessment: true,
      checkInList: true,
      dailyPersonnelSignIn: true,
      resourceRequestForm: true,
    },
    widgetOrder: [
      'assignmentList',
      'workAnalysisMatrix',
      'operationalPlanningWorksheet',
      'iapSafetyAnalysis',
      'incidentStatusSummary',
      'openActionsTracker',
      'commsResourceAvailability',
      'facilityNeedsAssessment',
      'checkInList',
      'dailyPersonnelSignIn',
      'resourceRequestForm',
    ],
    code: '#4E342E', // Dark Brown
    agenda: [],
  },
  {
    title: PLANNING_PHASE.PLANNING_MEETING,
    description: ' ',
    widgets: {
      agenda: true,
      workAnalysisMatrix: true,
      operationalPlanningWorksheet: true,
      openActionsTracker: true,
      incidentObjectives: true,
      incidentStatusSummary: true,
    },
    widgetOrder: [
      'agenda',
      'incidentObjectives',
      'openActionsTracker',
      'workAnalysisMatrix',
      'operationalPlanningWorksheet',
      'incidentStatusSummary',
    ],
    code: '#FFB300', // Amber
    agenda: [
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.ROLL_CALL_GROUND_RULES,
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Provide brief opening remarks.',
      },
      {
        role: RESPONSIBLE_ROLE.SITL,
        item: AGENDA_ITEM_TEXT.SITUATION_BRIEF,
      },
      {
        role: 'SOFR',
        item: 'Provide status briefing highlighting any safety issues for the current operational period.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: "Review Command's incident priorities, decisions, and objectives (ICS 202).",
      },
      {
        role: 'OSC/ISC',
        item: 'Provide briefing on current operations followed by an overview on the proposed plan including strategies, tactics/work assignments, resource requirements, contingencies, Operations Section organization structure, and support facilities.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Review proposed plan to ensure that Command direction, priorities, and operational objectives are met.',
      },
      {
        role: 'LSC',
        item: 'Solicit input: transportation, communications, services, staffing and supply concerns.',
      },
      {
        role: 'FSC',
        item: 'Solicit input: funding issues.',
      },
      {
        role: 'PIO',
        item: 'Solicit input: public affairs and public information.',
      },
      {
        role: 'LOFR',
        item: 'Solicit input: liaison or interagency issues.',
      },
      {
        role: 'SOFR',
        item: 'Solicit input: safety issues.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Ask each member of Command and General Staff if they support the plan.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: "Request Command's comments and approval of the plan as presented. IC/UC may provide final comments.",
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Issue assignments to appropriate IMT members for developing IAP support documentation along with deadlines.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.OPEN_ACTION_TRACKER,
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Identify time for the Operations Briefing.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: AGENDA_ITEM_TEXT.BRIEF_CLOSING_COMMENTS,
      },
    ],
  },
  {
    title: PLANNING_PHASE.IAP_PREP_APPROVAL,
    description: ' ',
    widgets: {
      organizationAssignmentList: true,
      safetyPlan: true,
      incidentStatusSummary: true,
      assignmentList: true,
      openActionsTracker: true,
      incidentRadioCommunicationsPlan: true,
      medicalPlan: true,
      incidentObjectives: true,
      airOperationsSummary: true,
      uasAirOperationsSummary: true,
      personnelPerformanceRating: true,
      incidentOrganizationChart: true,
    },
    widgetOrder: [
      'incidentObjectives',
      'organizationAssignmentList',
      'safetyPlan',
      'assignmentList',
      'incidentRadioCommunicationsPlan',
      'medicalPlan',
      'incidentStatusSummary',
      'openActionsTracker',
      'airOperationsSummary',
      'uasAirOperationsSummary',
      'personnelPerformanceRating',
      'incidentOrganizationChart',
    ],
    code: '#8E24AA', // Purple
    agenda: [],
  },
  {
    title: PLANNING_PHASE.OPERATIONS_BRIEFING,
    description: ' ',
    widgets: {
      agenda: true,
      incidentActionPlan: true,
      tentativeReleaseList: true,
      activityLogDocument: true,
      demobilizationCheckOut: true,
      demobTrackingTable: true,
    },
    widgetOrder: [
      'agenda',
      'incidentActionPlan',
      'tentativeReleaseList',
      'activityLogDocument',
      'demobilizationCheckOut',
      'demobTrackingTable',
    ],
    code: '#3949AB', // Indigo
    agenda: [
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.ROLL_CALL_GROUND_RULES,
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: 'Provide brief Command remarks.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: AGENDA_ITEM_TEXT.REVIEW_IC_UC_OBJECTIVES_CIR_IAP_CHANGES,
      },
      {
        role: RESPONSIBLE_ROLE.SITL,
        item: 'Provide Situation Briefing. Discuss current response actions and accomplishments.',
      },
      {
        role: 'OSC',
        item: 'Brief Operations Section personnel on strategies, tactics, and ICS 204 assignments.',
      },
      {
        role: 'SOFR',
        item: 'Provide safety briefing.',
      },
      {
        role: 'LSC',
        item: 'Transportation, communications, and supply updates.',
      },
      {
        role: 'FSC',
        item: 'Administrative and fiscal issues.',
      },
      {
        role: 'PIO',
        item: 'Public affairs and public information issues.',
      },
      {
        role: 'LOFR',
        item: 'Interagency issues.',
      },
      {
        role: RESPONSIBLE_ROLE.PSC,
        item: 'Solicit final comments and adjourn briefing.',
      },
      {
        role: RESPONSIBLE_ROLE.IC_UC,
        item: AGENDA_ITEM_TEXT.BRIEF_CLOSING_COMMENTS,
      },
    ],
  },
];
